import React, { useCallback, useEffect } from 'react';
import { Journeys, useCrossFlow } from '@atlassiansox/cross-flow-support';

import { ButtonItem } from '@atlaskit/menu';
import { ErrorBoundary } from '@atlassian/jira-error-boundary';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { useCustomAnalyticEvents } from '../../common';
import {
	PACKAGE_NAME,
	EVENT_SOURCE_EXPLORE,
	TEAM_NAME,
	type CohortType,
} from '../../common/constants';

export interface ExploreMoreSectionProps {
	isEligible: boolean;
	sessionId: string;
	cohort: CohortType | string;
}

const ExploreMoreSection = ({ isEligible, sessionId, cohort }: ExploreMoreSectionProps) => {
	const { fireViewedEvent, fireClickEvent } = useCustomAnalyticEvents({ cohort, sessionId });
	const crossFlow = useCrossFlow();

	const onLinkClick = useCallback(async () => {
		fireClickEvent({ eventName: EVENT_SOURCE_EXPLORE });

		try {
			if (!crossFlow.isEnabled) {
				throw new Error('Cross Flow not enabled');
			}

			await crossFlow.api.open({
				sourceComponent: EVENT_SOURCE_EXPLORE,
				sourceContext: EVENT_SOURCE_EXPLORE,
				journey: Journeys.DISCOVER,
				extraAnalyticsAttributes: {
					recommendationSessionId: sessionId,
				},
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: `openCrossFlow.${EVENT_SOURCE_EXPLORE}`,
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
			});
		}
	}, [sessionId, crossFlow, fireClickEvent]);

	useEffect(() => {
		if (isEligible) {
			fireViewedEvent({});
		}
	}, [fireViewedEvent, isEligible]);

	if (!isEligible) {
		return null;
	}

	return <ButtonItem onClick={onLinkClick}>Explore more Atlassian products</ButtonItem>;
};

const ExploreMoreSectionWithBoundary = ({
	isEligible,
	sessionId,
	cohort,
}: ExploreMoreSectionProps) => (
	<ErrorBoundary
		onError={(_, error) => {
			fireErrorAnalytics({
				meta: {
					id: `errorBoundary.${EVENT_SOURCE_EXPLORE}`,
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
			});
		}}
	>
		<ContextualAnalyticsData sourceName={EVENT_SOURCE_EXPLORE} sourceType={SCREEN}>
			<ExploreMoreSection isEligible={isEligible} sessionId={sessionId} cohort={cohort} />
		</ContextualAnalyticsData>
	</ErrorBoundary>
);

export { ExploreMoreSectionWithBoundary as ExploreMoreSection };
